<script lang="ts" setup>
import UiAuditInfo from '@/components/UiAuditInfo.vue';
import { Permission, ScriptModel } from '@/types';
import { CheckCircleIcon, PencilAltIcon, XCircleIcon } from '@heroicons/vue/outline';
import { PropType } from 'vue';

const props = defineProps({
    modelValue: {
        type: Object as PropType<ScriptModel>,
        required: true,
    },
});

const trimText = (text: string, limit: number) => {
    if (text.length <= limit) {
        return text;
    } else {
        return text.slice(0, limit - 3) + '...';
    }
};
const emit = defineEmits(['activate', 'inactivate']);
const handleActivateClick = () => emit('activate', props.modelValue);
const handleInactivateClick = () => emit('inactivate', props.modelValue);
</script>

<template>
    <UiPanel class="hover:bg-white">
        <div class="flex items-center justify-between">
            <div class="flex flex-1 items-center">
                <component
                    :is="$can(Permission.VIEW_SCRIPT) ? 'RouterLink' : 'span'"
                    :to="{
                        name: 'ViewScriptById',
                        params: {
                            id: modelValue?.id,
                        },
                    }"
                    class="text-md text-primary truncate font-medium"
                >
                    <span>
                        {{ trimText(modelValue.clientUrl, 25) }}
                    </span>
                </component>
                <UiIsActiveBadge :model-value="modelValue" class="ml-2 text-xs" />
            </div>
            <div v-if="$can(Permission.EDIT_SCRIPT)" class="flex space-x-1">
                <UiActionButton
                    v-if="modelValue.isActive"
                    v-tippy="$t('core.actions.Inactivate')"
                    variant="danger"
                    @click="handleInactivateClick"
                >
                    <span class="sr-only"> {{ $t('core.actions.Inactivate') }}</span>
                    <XCircleIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
                <UiActionButton
                    v-else
                    v-tippy="$t('core.actions.Activate')"
                    variant="success"
                    @click="handleActivateClick"
                >
                    <span class="sr-only"> {{ $t('core.actions.Activate') }}</span>
                    <CheckCircleIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
                <UiActionButton
                    v-tippy="$t('core.actions.Edit')"
                    variant="primary"
                    class="rounded-full"
                    :to="{
                        name: 'EditScriptById',
                        params: {
                            id: modelValue?.id,
                        },
                    }"
                >
                    <span class="sr-only"> {{ $t('core.actions.Edit') }}</span>
                    <PencilAltIcon class="h-6 w-6" aria-hidden="true" />
                </UiActionButton>
            </div>
        </div>
        <UiAuditInfo :model-value="modelValue" />
    </UiPanel>
</template>
